/**
 * History Item Component
 * Individual history entry in the list - Simple list design matching Figma
 *
 * Follows SOLID principles:
 * - SRP: Composed of smaller, focused components (Text, Date, Actions, Content)
 * - OCP: Extensible through component composition
 * - ISP: Minimal, focused interface
 * - DIP: Depends on abstractions (composed components)
 */

import React, { useState } from 'react';
import { SearchEntry } from '@/store/chatHistoryStore';
import {
  spacing,
  borders,
  strokeColors,
  brandColors,
  backgroundColors,
} from '@/design-system/utils';
import {
  formatHistoryDate,
  formatHistoryTime,
  getChatTitle,
  getChatDescription,
} from '@/utils/historyHelpers';
import { HistoryItemContent } from './HistoryItemContent';

interface HistoryItemProps {
  entry: SearchEntry;
  index: number;
  totalEntries: number;
  onClick: () => void;
  onShare?: (index: number) => void;
  onDelete?: (index: number) => void;
}

/**
 * History Item Container
 * Main container for history item with border and layout styling
 */
export const HistoryItem: React.FC<HistoryItemProps> = ({
  entry,
  index,
  totalEntries,
  onClick,
  onShare,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const title = getChatTitle(entry);
  const description = getChatDescription(entry);
  const formattedDate = formatHistoryDate(entry);
  const formattedTime = formatHistoryTime(entry);

  const handleShare = () => {
    setIsHovered(false);
    onShare?.(index);
  };

  const handleDelete = () => {
    setIsHovered(false);
    onDelete?.(index);
  };

  const handleItemClick = (e: React.MouseEvent) => {
    // Don't trigger item click if clicking on action menu, dropdown, or ellipsis button
    const target = e.target as HTMLElement;
    const clickedElement = target.closest(
      'button, [role="menuitem"], [data-radix-dropdown-menu-trigger], [data-radix-dropdown-menu-content]'
    );

    // Check if click is on action menu area (button with aria-label or parent of ellipsis icon)
    const isActionMenuClick =
      clickedElement?.getAttribute('aria-label') === 'More options' ||
      clickedElement?.closest('button[aria-label="More options"]') ||
      target.closest('[role="menuitem"]') ||
      target.closest('[data-radix-dropdown-menu-trigger]') ||
      target.closest('[data-radix-dropdown-menu-content]') ||
      (target.tagName === 'IMG' &&
        target.getAttribute('src')?.includes('ellipsis')) ||
      target.closest('img[src*="ellipsis"]');

    if (isActionMenuClick) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    onClick();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        paddingLeft: spacing[1],
        paddingRight: spacing[1],
        paddingTop: spacing[4],
        paddingBottom: spacing[4],
        borderRadius: borders.radius.md,
        borderBottom:
          index < totalEntries - 1
            ? `${borders.width.thin} solid ${strokeColors.light}`
            : 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        width: '100%',
        backgroundColor: isHovered
          ? brandColors.tertiary
          : backgroundColors.primary,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleItemClick}
    >
      <HistoryItemContent
        title={title}
        description={description}
        date={formattedDate}
        time={formattedTime}
        onShare={onShare ? handleShare : undefined}
        onDelete={onDelete ? handleDelete : undefined}
      />
    </div>
  );
};
